import {
  createContext,
  useContext,
  useId,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';

import './Glossary.css';

/**
 * Plain-language glosses for the vocabulary this document assumes. A reviewer
 * who knows all of it loses nothing; one who does not should never have to
 * leave the page to find out what a static factor is.
 */
export const GLOSSARY: { term: string; aliases?: string[]; definition: string }[] = [
  {
    term: 'RRP Matrix',
    aliases: ['RRP matrix', 'RRP'],
    definition:
      'Risk, Responsibility and Performance Matrix — Table 3-1 of the FEMP M&V Guidelines. Sixteen rows across Financial, Operational and Performance, each recording which party carries a particular risk. Every finding names the row it belongs to.',
  },
  {
    term: 'Arithmetic',
    definition:
      'Ceiling re-did the plan’s own maths. You can check it on paper.',
  },
  {
    term: 'Audit precedent',
    definition:
      'A named federal audit records this exact cause producing a real dispute or dollar loss.',
  },
  {
    term: 'Guidance warning',
    definition:
      'FEMP’s own guidance warns about this. No audit case is attached to it yet.',
  },
  {
    term: 'ECM',
    aliases: ['ECMs'],
    definition:
      'Energy Conservation Measure — one funded improvement within the project, such as a lighting retrofit or a chiller replacement. A plan carries a separate M&V section for each.',
  },
  {
    term: 'IGA',
    definition:
      'Investment Grade Audit — the detailed engineering study, produced before award, that establishes the baseline and the projected savings the contract is written against.',
  },
  {
    term: 'stipulation',
    aliases: ['stipulated', 'stipulate', 'stipulations'],
    definition:
      'A value both parties agree to treat as fixed for the contract term, whether or not it keeps matching reality. FEMP notes stipulations "have the potential to shift risk to the customer".',
  },
  {
    term: 'non-routine adjustment',
    aliases: ['non-routine adjustments', 'NRA'],
    definition:
      'A correction to the baseline when something changes that the savings model does not track — an occupancy change, a new tenant, a building extension. Whoever computes it controls the payment, which is why the trigger is supposed to be pre-agreed.',
  },
  {
    term: 'static factor',
    aliases: ['static factors'],
    definition:
      'A condition the savings calculation assumes will hold still: floor area, occupancy, operating schedule. FEMP requires the plan to list them and state the threshold at which a change triggers a non-routine adjustment.',
  },
];

interface GlossaryRegistry {
  /** Returns true if this instance is the first to claim the term. */
  claim(term: string, instanceId: string): boolean;
}

const GlossaryContext = createContext<GlossaryRegistry | null>(null);

/**
 * Tracks which term has been glossed already, so only the first appearance
 * carries the definition. Claims are keyed by a stable instance id, which keeps
 * the decision steady across re-renders and React's double-invoked renders.
 */
export function GlossaryProvider({ children }: { children: ReactNode }) {
  const claims = useRef(new Map<string, string>());

  const registry = useMemo<GlossaryRegistry>(
    () => ({
      claim(term, instanceId) {
        const held = claims.current.get(term);
        if (held === undefined) {
          claims.current.set(term, instanceId);
          return true;
        }
        return held === instanceId;
      },
    }),
    [],
  );

  return (
    <GlossaryContext.Provider value={registry}>{children}</GlossaryContext.Provider>
  );
}

function definitionFor(term: string): string | null {
  const entry = GLOSSARY.find(
    (g) =>
      g.term.toLowerCase() === term.toLowerCase() ||
      g.aliases?.some((a) => a.toLowerCase() === term.toLowerCase()),
  );
  return entry ? entry.definition : null;
}

function canonicalFor(term: string): string | null {
  const entry = GLOSSARY.find(
    (g) =>
      g.term.toLowerCase() === term.toLowerCase() ||
      g.aliases?.some((a) => a.toLowerCase() === term.toLowerCase()),
  );
  return entry ? entry.term : null;
}

/**
 * One glossed term. Definition appears on hover and on keyboard focus — it is a
 * focusable button with the definition rendered in the DOM, not an icon-only
 * tooltip, so it works without a mouse and reads to a screen reader.
 */
function Term({ children }: { children: string }) {
  const registry = useContext(GlossaryContext);
  const instanceId = useId();
  const canonical = canonicalFor(children);
  const definition = canonical ? definitionFor(children) : null;

  const isFirst = canonical && registry ? registry.claim(canonical, instanceId) : false;

  if (!definition || !isFirst) return <>{children}</>;

  return (
    <span className="term">
      <button type="button" className="term__word" aria-describedby={`${instanceId}-def`}>
        {children}
      </button>
      <span className="term__def" id={`${instanceId}-def`} role="note">
        <span className="term__def-head">{canonical}</span>
        {definition}
      </span>
    </span>
  );
}

/** Terms are matched on word boundaries, longest first. */
const MATCHER = new RegExp(
  `\\b(${GLOSSARY.flatMap((g) => [g.term, ...(g.aliases ?? [])])
    .sort((a, b) => b.length - a.length)
    .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|')})\\b`,
  'g',
);

/**
 * Wraps glossary terms found in a plain string. Body copy is passed through
 * this so a gloss appears wherever the vocabulary genuinely occurs, rather than
 * being sprinkled by hand.
 */
export function Glossed({ children }: { children: string }) {
  const parts = useMemo(() => {
    const out: { text: string; term: boolean }[] = [];
    let last = 0;
    MATCHER.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = MATCHER.exec(children)) !== null) {
      if (m.index > last) out.push({ text: children.slice(last, m.index), term: false });
      out.push({ text: m[0], term: true });
      last = m.index + m[0].length;
    }
    if (last < children.length) out.push({ text: children.slice(last), term: false });
    return out;
  }, [children]);

  return (
    <>
      {parts.map((part, i) =>
        part.term ? (
          <Term key={i}>{part.text}</Term>
        ) : (
          <span key={i}>{part.text}</span>
        ),
      )}
    </>
  );
}

/** Every term and definition, for the sheet's own reference block and export. */
export function glossaryEntries() {
  return GLOSSARY.map(({ term, definition }) => ({ term, definition }));
}
